import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { toast } from "sonner";
import {
  getExpenses, createExpense, updateExpense, deleteExpense,
  getExpenseConfig, addExpenseCategory, renameExpenseCategory, deleteExpenseCategory,
  getRecurringExpenses, createRecurringExpense, updateRecurringExpense, deleteRecurringExpense,
} from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import {
  Wallet, Plus, Trash2, Pencil, SlidersHorizontal, Check, X,
  TrendingUp, TrendingDown, Minus, Receipt, Repeat, Pause, Play,
} from "lucide-react";

const CURRENCIES = ["RON", "EUR", "USD"];
const CHART_COLORS = ["#0E7490", "#14B8A6", "#F59E0B", "#3B82F6", "#22C55E", "#A855F7", "#EF4444", "#8B5CF6"];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" };
const BAR_CURSOR = { fill: "hsl(var(--secondary))" };
const CHART_MARGIN = { left: -12, right: 8, top: 6 };

const today = () => new Date().toISOString().slice(0, 10);

const money = (v, cur = "RON") =>
  `${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

// ---- date bucketing helpers ----
const startOfWeek = (d) => {
  const dt = new Date(d + "T00:00:00Z");
  const day = (dt.getUTCDay() + 6) % 7; // Monday = 0
  dt.setUTCDate(dt.getUTCDate() - day);
  return dt.toISOString().slice(0, 10);
};
const addDaysISO = (iso, n) => {
  const dt = new Date(iso + "T00:00:00Z");
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};
const monthKey = (d) => d.slice(0, 7); // YYYY-MM
const addMonths = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
};
const shortDate = (iso) => {
  const dt = new Date(iso + "T00:00:00Z");
  return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

export default function Expenses() {
  const { t } = useT();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("weekly"); // weekly | monthly
  const [currency, setCurrency] = useState(null); // active currency filter
  const [dialog, setDialog] = useState(null); // {} new, object edit
  const [configOpen, setConfigOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [ruleDialog, setRuleDialog] = useState(null); // recurring rule being edited
  const [delExpense, setDelExpense] = useState(null);
  // Table filters
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("all"); // all | this_week | this_month | last_month | this_year | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [ex, cfg] = await Promise.all([
        getExpenses(),
        getExpenseConfig().catch(() => ({ categories: [] })),
      ]);
      setExpenses(ex || []);
      setCategories(cfg?.categories || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Currencies actually present in the data (+ default RON).
  const currencyOptions = useMemo(() => {
    const set = new Set(expenses.map((e) => e.currency).filter(Boolean));
    if (set.size === 0) set.add("RON");
    return Array.from(set);
  }, [expenses]);

  // Pick a sensible default currency (most used) once data is loaded.
  useEffect(() => {
    if (currency && currencyOptions.includes(currency)) return;
    const counts = {};
    expenses.forEach((e) => { counts[e.currency] = (counts[e.currency] || 0) + 1; });
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    setCurrency(best || currencyOptions[0] || "RON");
  }, [expenses, currencyOptions, currency]);

  const scoped = useMemo(
    () => expenses.filter((e) => e.currency === currency),
    [expenses, currency]
  );

  // Build buckets for the trend chart + current/previous totals.
  const analytics = useMemo(() => {
    const cur = currency || "RON";
    const bucketOf = (d) => (period === "weekly" ? startOfWeek(d) : monthKey(d));
    // Reference bucket = the one containing today.
    const todayBucket = bucketOf(today());
    const nBuckets = period === "weekly" ? 8 : 6;
    const keys = [];
    for (let i = nBuckets - 1; i >= 0; i--) {
      keys.push(period === "weekly" ? addDaysISO(todayBucket, -7 * i) : addMonths(todayBucket, -i));
    }
    const sums = Object.fromEntries(keys.map((k) => [k, 0]));
    const catCurrent = {};
    scoped.forEach((e) => {
      const k = bucketOf(e.date);
      if (k in sums) sums[k] += Number(e.amount || 0);
      if (k === todayBucket) catCurrent[e.category] = (catCurrent[e.category] || 0) + Number(e.amount || 0);
    });
    const series = keys.map((k) => ({
      key: k,
      label: period === "weekly" ? shortDate(k) : monthLabel(k),
      amount: Math.round((sums[k] || 0) * 100) / 100,
    }));
    const currentTotal = sums[todayBucket] || 0;
    const prevKey = period === "weekly" ? addDaysISO(todayBucket, -7) : addMonths(todayBucket, -1);
    const prevTotal = sums[prevKey] || 0;
    let deltaPct = null;
    if (prevTotal > 0) deltaPct = ((currentTotal - prevTotal) / prevTotal) * 100;
    else if (currentTotal > 0) deltaPct = 100;
    const byCategory = Object.entries(catCurrent)
      .map(([category, amount]) => ({ category, amount: Math.round(amount * 100) / 100 }))
      .sort((a, b) => b.amount - a.amount);
    const allTimeTotal = scoped.reduce((s, e) => s + Number(e.amount || 0), 0);
    return { cur, series, currentTotal, prevTotal, deltaPct, byCategory, allTimeTotal };
  }, [scoped, period, currency]);

  // Resolve the active period filter to a [from, to] date window (inclusive, ISO).
  const periodRange = useMemo(() => {
    const now = today();
    if (filterPeriod === "this_week") return [startOfWeek(now), addDaysISO(startOfWeek(now), 6)];
    if (filterPeriod === "this_month") {
      const ym = monthKey(now);
      return [`${ym}-01`, addDaysISO(`${addMonths(ym, 1)}-01`, -1)];
    }
    if (filterPeriod === "last_month") {
      const ym = addMonths(monthKey(now), -1);
      return [`${ym}-01`, addDaysISO(`${monthKey(now)}-01`, -1)];
    }
    if (filterPeriod === "this_year") return [`${now.slice(0, 4)}-01-01`, `${now.slice(0, 4)}-12-31`];
    if (filterPeriod === "custom") return [customFrom || null, customTo || null];
    return [null, null];
  }, [filterPeriod, customFrom, customTo]);

  // Rows shown in the table: currency-scoped, then filtered by category + period.
  const tableRows = useMemo(() => {
    const [from, to] = periodRange;
    return scoped.filter((e) => {
      if (filterCategory !== "all" && e.category !== filterCategory) return false;
      if (from && e.date < from) return false;
      if (to && e.date > to) return false;
      return true;
    });
  }, [scoped, filterCategory, periodRange]);

  const tableTotal = useMemo(
    () => tableRows.reduce((s, e) => s + Number(e.amount || 0), 0),
    [tableRows]
  );
  const filtersActive = filterCategory !== "all" || filterPeriod !== "all";

  const removeExpense = async () => {
    if (!delExpense) return;
    try {
      await deleteExpense(delExpense.id);
      toast.success(t("expenses.deleted"));
      setDelExpense(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const catBreakdownMax = Math.max(1, ...analytics.byCategory.map((c) => c.amount));

  return (
    <Layout>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Wallet className="h-6 w-6 text-[hsl(var(--brand))]" /> {t("expenses.title")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("expenses.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setRecurringOpen(true)} data-testid="exp-open-recurring-button">
            <Repeat className="mr-1.5 h-4 w-4" /> {t("expenses.recurringBtn")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setConfigOpen(true)} data-testid="exp-open-config-button">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> {t("expenses.manageCategories")}
          </Button>
          <Button size="sm" onClick={() => setDialog({})} data-testid="exp-add-button">
            <Plus className="mr-1.5 h-4 w-4" /> {t("expenses.addExpense")}
          </Button>
        </div>
      </div>

      {/* Controls: period + currency */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="exp-period-tabs">
            <TabsTrigger value="weekly" data-testid="exp-period-weekly">{t("expenses.weekly")}</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="exp-period-monthly">{t("expenses.monthly")}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">{t("expenses.currency")}</Label>
          <Select value={currency || "RON"} onValueChange={setCurrency}>
            <SelectTrigger className="h-9 w-[110px]" data-testid="exp-currency-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overview: total + movement, trend chart, category breakdown */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Total spending + movement */}
        <Card data-testid="exp-total-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground">
              <Receipt className="h-4 w-4 text-[hsl(var(--brand))]" />
              {period === "weekly" ? t("expenses.thisWeek") : t("expenses.thisMonth")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold tracking-tight text-foreground" data-testid="exp-total-value">
              {money(analytics.currentTotal, analytics.cur)}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <DeltaBadge deltaPct={analytics.deltaPct} />
              <span className="text-xs text-muted-foreground">
                {t("expenses.vsPrevious")}: {money(analytics.prevTotal, analytics.cur)}
              </span>
            </div>
            <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
              {t("expenses.allTime")}: <span className="font-medium text-foreground">{money(analytics.allTimeTotal, analytics.cur)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Trend chart */}
        <Card className="lg:col-span-2" data-testid="exp-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              {t("expenses.trendTitle")} · {analytics.cur}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.series.some((s) => s.amount > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.series} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" width={54} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR}
                    formatter={(v) => [money(v, analytics.cur), t("expenses.spent")]} />
                  <Bar dataKey="amount" radius={[6, 6, 0, 0]} fill="#0E7490" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[200px] flex-col items-center justify-center text-center text-sm text-muted-foreground" data-testid="exp-trend-empty">
                <TrendingUp className="mb-2 h-8 w-8 text-muted-foreground/40" />
                {t("expenses.noData")}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category breakdown for current period */}
      <Card className="mt-5" data-testid="exp-breakdown-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            {t("expenses.byCategory")} · {period === "weekly" ? t("expenses.thisWeek") : t("expenses.thisMonth")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.byCategory.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">{t("expenses.noData")}</p>
          ) : (
            <div className="space-y-2.5">
              {analytics.byCategory.map((c, i) => (
                <div key={c.category} className="flex items-center gap-3" data-testid={`exp-cat-bar-${i}`}>
                  <span className="w-32 shrink-0 truncate text-sm font-medium text-foreground">{c.category}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full" style={{ width: `${(c.amount / catBreakdownMax) * 100}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  </div>
                  <span className="w-28 shrink-0 text-right font-mono text-sm text-foreground">{money(c.amount, analytics.cur)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expenses table */}
      <Card className="mt-5">
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("expenses.allExpenses")} ({tableRows.length})
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="exp-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("expenses.allCategories")}</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="exp-filter-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("expenses.periodAll")}</SelectItem>
                  <SelectItem value="this_week">{t("expenses.periodThisWeek")}</SelectItem>
                  <SelectItem value="this_month">{t("expenses.periodThisMonth")}</SelectItem>
                  <SelectItem value="last_month">{t("expenses.periodLastMonth")}</SelectItem>
                  <SelectItem value="this_year">{t("expenses.periodThisYear")}</SelectItem>
                  <SelectItem value="custom">{t("expenses.periodCustom")}</SelectItem>
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={() => { setFilterCategory("all"); setFilterPeriod("all"); setCustomFrom(""); setCustomTo(""); }} data-testid="exp-filter-clear">
                  <X className="mr-1 h-4 w-4" /> {t("expenses.clearFilters")}
                </Button>
              )}
            </div>
          </div>
          {filterPeriod === "custom" && (
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-xs text-muted-foreground">{t("expenses.from")}</Label>
              <Input type="date" className="h-9 w-[160px]" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="exp-filter-from" />
              <Label className="text-xs text-muted-foreground">{t("expenses.to")}</Label>
              <Input type="date" className="h-9 w-[160px]" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="exp-filter-to" />
            </div>
          )}
          {tableRows.length > 0 && (
            <div className="text-sm text-muted-foreground" data-testid="exp-filtered-total">
              {filtersActive ? t("expenses.filteredTotal") : t("common.total")}: <span className="font-semibold text-foreground">{money(tableTotal, currency || "RON")}</span>
              <span className="ml-1 text-xs">({tableRows.length})</span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : scoped.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="exp-empty">
              <Wallet className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("expenses.empty")}</p>
              <Button size="sm" className="mt-4" onClick={() => setDialog({})} data-testid="exp-add-empty">
                <Plus className="mr-1.5 h-4 w-4" /> {t("expenses.addExpense")}
              </Button>
            </div>
          ) : tableRows.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="exp-no-results">
              <Wallet className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("expenses.noResults")}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => { setFilterCategory("all"); setFilterPeriod("all"); setCustomFrom(""); setCustomTo(""); }} data-testid="exp-no-results-clear">
                <X className="mr-1.5 h-4 w-4" /> {t("expenses.clearFilters")}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("expenses.date")}</TableHead>
                    <TableHead>{t("expenses.category")}</TableHead>
                    <TableHead>{t("expenses.vendor")}</TableHead>
                    <TableHead>{t("common.notes")}</TableHead>
                    <TableHead className="text-right">{t("expenses.amount")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.map((e) => (
                    <TableRow key={e.id} data-testid={`exp-row-${e.id}`}>
                      <TableCell className="whitespace-nowrap text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          {formatDate(e.date)}
                          {e.recurring_id && <Repeat className="h-3.5 w-3.5 text-[hsl(var(--brand))]" title={t("expenses.fromRecurring")} data-testid={`exp-recurring-badge-${e.id}`} />}
                        </span>
                      </TableCell>
                      <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                      <TableCell className="text-sm">{e.vendor || "—"}</TableCell>
                      <TableCell className="max-w-[240px] truncate text-sm text-muted-foreground">{e.notes || "—"}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{money(e.amount, e.currency)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" title={t("common.edit")} onClick={() => setDialog(e)} data-testid={`exp-edit-${e.id}`}>
                          <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t("common.delete")} onClick={() => setDelExpense(e)} data-testid={`exp-delete-${e.id}`}>
                          <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {dialog && (
        <ExpenseDialog key={dialog.id ?? "new"} expense={dialog} categories={categories}
          defaultCurrency={currency || "RON"}
          onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      )}
      {ruleDialog && (
        <ExpenseDialog key={`rule-${ruleDialog.id}`} rule={ruleDialog} categories={categories}
          defaultCurrency={currency || "RON"}
          onClose={() => setRuleDialog(null)} onSaved={() => { setRuleDialog(null); load(); }} />
      )}
      {configOpen && (
        <CategoryManager categories={categories}
          onClose={() => setConfigOpen(false)}
          onChanged={(cats) => { if (cats) setCategories(cats); load(); }} />
      )}
      {recurringOpen && (
        <RecurringManager
          onEditRule={(r) => { setRecurringOpen(false); setRuleDialog(r); }}
          onClose={() => setRecurringOpen(false)}
          onChanged={() => load()} />
      )}

      <AlertDialog open={!!delExpense} onOpenChange={(o) => !o && setDelExpense(null)}>
        <AlertDialogContent data-testid="exp-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("expenses.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("expenses.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeExpense} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="exp-delete-confirm">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

const DeltaBadge = ({ deltaPct }) => {
  const { t } = useT();
  if (deltaPct == null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid="exp-delta">
        <Minus className="h-3.5 w-3.5" /> {t("expenses.noChange")}
      </span>
    );
  }
  const up = deltaPct > 0;
  const flat = Math.abs(deltaPct) < 0.05;
  if (flat) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid="exp-delta">
        <Minus className="h-3.5 w-3.5" /> 0%
      </span>
    );
  }
  // Spending going UP is bad (red); going DOWN is good (green).
  const cls = up
    ? "bg-[hsl(var(--qc-bad-bg))] text-[hsl(var(--danger))]"
    : "bg-[hsl(var(--qc-ok-bg))] text-[hsl(var(--success))]";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid="exp-delta">
      <Icon className="h-3.5 w-3.5" /> {up ? "+" : ""}{deltaPct.toFixed(1)}%
    </span>
  );
};

function ExpenseDialog({ expense, rule, categories, defaultCurrency, onClose, onSaved }) {
  const { t } = useT();
  const isRule = !!rule;                       // editing an existing recurring rule
  const editingExpense = !!expense?.id;        // editing an existing single expense
  const src = rule || expense || {};
  const [amount, setAmount] = useState(src.amount ?? "");
  const [currency, setCurrency] = useState(src.currency || defaultCurrency || "RON");
  const [category, setCategory] = useState(src.category || categories?.[0] || "");
  const [date, setDate] = useState((src.date || src.start_date || today()).slice(0, 10));
  const [vendor, setVendor] = useState(src.vendor || "");
  const [notes, setNotes] = useState(src.notes || "");
  const [recurring, setRecurring] = useState(isRule);          // toggle for NEW expenses
  const [frequency, setFrequency] = useState(src.frequency || "monthly");
  const [endDate, setEndDate] = useState(src.end_date || "");
  const [saving, setSaving] = useState(false);

  const canToggleRecurring = !isRule && !editingExpense;       // only when adding a brand-new item
  const showRecurringFields = recurring || isRule;

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error(t("expenses.amountRequired")); return; }
    if (!category) { toast.error(t("expenses.categoryRequired")); return; }
    if (showRecurringFields && endDate && date && endDate < date && !isRule) {
      toast.error(t("expenses.endAfterStart")); return;
    }
    setSaving(true);
    try {
      if (isRule) {
        await updateRecurringExpense(rule.id, {
          amount: amt, currency, category, vendor: vendor.trim(), notes: notes.trim(),
          frequency, end_date: endDate || null,
        });
        toast.success(t("expenses.ruleUpdated"));
      } else if (recurring) {
        await createRecurringExpense({
          amount: amt, currency, category, vendor: vendor.trim(), notes: notes.trim(),
          frequency, start_date: date || today(), end_date: endDate || null,
        });
        toast.success(t("expenses.ruleCreated"));
      } else if (editingExpense) {
        await updateExpense(expense.id, { amount: amt, currency, category, date: date || today(), vendor: vendor.trim(), notes: notes.trim() });
        toast.success(t("expenses.updated"));
      } else {
        await createExpense({ amount: amt, currency, category, date: date || today(), vendor: vendor.trim(), notes: notes.trim() });
        toast.success(t("expenses.created"));
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setSaving(false); }
  };

  const dialogTitle = isRule
    ? t("expenses.editRule")
    : editingExpense ? t("expenses.editExpense") : t("expenses.addExpense");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="exp-dialog">
        <DialogHeader><DialogTitle>{dialogTitle}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("expenses.amount")}</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="text-right font-mono" placeholder="0.00" data-testid="exp-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("expenses.currency")}</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="exp-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("expenses.category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="exp-category-select"><SelectValue placeholder={t("expenses.pickCategory")} /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!isRule && (
              <div className="space-y-1.5">
                <Label>{recurring ? t("expenses.startDate") : t("expenses.date")}</Label>
                <Input type="date" value={date} min={recurring ? today() : undefined} onChange={(e) => setDate(e.target.value)} data-testid="exp-date-input" />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t("expenses.vendor")}</Label>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder={t("expenses.vendorPlaceholder")} data-testid="exp-vendor-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="exp-notes-input" />
          </div>

          {canToggleRecurring && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-[hsl(var(--brand))]" />
                <div>
                  <div className="text-sm font-medium">{t("expenses.makeRecurring")}</div>
                  <div className="text-xs text-muted-foreground">{t("expenses.makeRecurringDesc")}</div>
                </div>
              </div>
              <Switch checked={recurring} onCheckedChange={setRecurring} data-testid="exp-recurring-toggle" />
            </div>
          )}

          {showRecurringFields && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-[hsl(var(--brand)/0.3)] bg-[hsl(var(--brand)/0.04)] p-3">
              <div className="space-y-1.5">
                <Label>{t("expenses.frequency")}</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="exp-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{t("expenses.freqWeekly")}</SelectItem>
                    <SelectItem value="monthly">{t("expenses.freqMonthly")}</SelectItem>
                    <SelectItem value="yearly">{t("expenses.freqYearly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("expenses.endDate")}</Label>
                <Input type="date" value={endDate || ""} min={date || today()} onChange={(e) => setEndDate(e.target.value)} data-testid="exp-enddate-input" />
              </div>
              {isRule && (
                <p className="col-span-2 text-xs text-muted-foreground">
                  {t("expenses.nextCharge")}: <span className="font-medium text-foreground">{rule.next_run ? formatDate(rule.next_run) : "—"}</span>
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="exp-save-button">{saving ? t("common.saving") : ((isRule || editingExpense) ? t("common.save") : t("common.create"))}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryManager({ categories, onClose, onChanged }) {
  const { t } = useT();
  const [adding, setAdding] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const add = async () => {
    const v = adding.trim();
    if (!v) return;
    setBusy(true);
    try {
      const res = await addExpenseCategory(v);
      setAdding("");
      onChanged(res.categories);
      toast.success(t("expenses.catAdded"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };
  const saveRename = async () => {
    const nv = editVal.trim();
    if (!nv || nv === editKey) { setEditKey(null); return; }
    setBusy(true);
    try {
      const res = await renameExpenseCategory(editKey, nv);
      setEditKey(null);
      onChanged(res.categories);
      toast.success(t("expenses.catRenamed"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      const res = await deleteExpenseCategory(confirmDel);
      setConfirmDel(null);
      onChanged(res.categories);
      toast.success(t("expenses.catDeleted"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="exp-config-dialog">
        <DialogHeader>
          <DialogTitle>{t("expenses.manageCategories")}</DialogTitle>
          <DialogDescription>{t("expenses.catSubtitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input value={adding} onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder={t("expenses.catPlaceholder")} data-testid="exp-cat-add-input" />
            <Button size="sm" onClick={add} disabled={busy || !adding.trim()} data-testid="exp-cat-add-button">
              <Plus className="mr-1 h-4 w-4" /> {t("expenses.catAdd")}
            </Button>
          </div>
          {categories.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("expenses.catEmpty")}</p>
          ) : (
            <div className="max-h-[45vh] space-y-1.5 overflow-y-auto thin-scroll pr-1">
              {categories.map((c) => (
                <div key={c} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5" data-testid={`exp-cat-row-${c}`}>
                  {editKey === c ? (
                    <>
                      <Input autoFocus className="h-8 flex-1 text-sm" value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditKey(null); }}
                        data-testid="exp-cat-edit-input" />
                      <Button variant="ghost" size="icon" onClick={saveRename} disabled={busy} title={t("common.save")}>
                        <Check className="h-4 w-4 text-[hsl(var(--brand))]" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setEditKey(null)} title={t("common.cancel")}>
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm">{c}</span>
                      <Button variant="ghost" size="icon" title={t("common.edit")} onClick={() => { setEditKey(c); setEditVal(c); }} data-testid={`exp-cat-edit-${c}`}>
                        <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                      </Button>
                      <Button variant="ghost" size="icon" title={t("common.delete")} onClick={() => setConfirmDel(c)} data-testid={`exp-cat-delete-${c}`}>
                        <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="exp-config-close">{t("common.close")}</Button>
        </DialogFooter>

        <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
          <AlertDialogContent data-testid="exp-cat-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("expenses.catDeleteTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("expenses.catDeleteDesc", { name: confirmDel || "" })}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="exp-cat-delete-confirm">{t("common.delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}


function RecurringManager({ onEditRule, onClose, onChanged }) {
  const { t } = useT();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const freqLabel = (f) => ({ weekly: t("expenses.freqWeekly"), monthly: t("expenses.freqMonthly"), yearly: t("expenses.freqYearly") }[f] || f);
  const money = (v, cur) => `${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

  const load = async () => {
    setLoading(true);
    try { setRules(await getRecurringExpenses()); }
    catch { setRules([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (r) => {
    setBusy(true);
    try {
      await updateRecurringExpense(r.id, { active: !r.active });
      toast.success(!r.active ? t("expenses.resumed") : t("expenses.paused"));
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await deleteRecurringExpense(confirmDel.id, false);
      toast.success(t("expenses.ruleDeleted"));
      setConfirmDel(null);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="exp-recurring-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Repeat className="h-5 w-5 text-[hsl(var(--brand))]" /> {t("expenses.recurringTitle")}</DialogTitle>
          <DialogDescription>{t("expenses.recurringSubtitle")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="py-6 text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : rules.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center" data-testid="exp-recurring-empty">
            <Repeat className="h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">{t("expenses.recurringEmpty")}</p>
          </div>
        ) : (
          <div className="max-h-[55vh] space-y-2 overflow-y-auto thin-scroll pr-1">
            {rules.map((r) => (
              <div key={r.id} className={`flex items-center gap-3 rounded-lg border p-3 ${r.active ? "border-border" : "border-dashed border-border opacity-70"}`} data-testid={`exp-rule-row-${r.id}`}>
                <div className="flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{r.category}</Badge>
                    <span className="font-mono font-semibold text-foreground">{money(r.amount, r.currency)}</span>
                    <Badge variant="secondary" className="gap-1"><Repeat className="h-3 w-3" />{freqLabel(r.frequency)}</Badge>
                    {!r.active && <Badge className="bg-secondary text-muted-foreground">{t("expenses.paused")}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.vendor ? `${r.vendor} · ` : ""}
                    {t("expenses.nextCharge")}: <span className="font-medium text-foreground">{r.active && r.next_run ? formatDate(r.next_run) : "—"}</span>
                    {r.end_date ? ` · ${t("expenses.ends")}: ${formatDate(r.end_date)}` : ` · ${t("expenses.forever")}`}
                  </div>
                </div>
                <Button variant="ghost" size="icon" title={r.active ? t("expenses.pause") : t("expenses.resume")} onClick={() => toggleActive(r)} disabled={busy} data-testid={`exp-rule-toggle-${r.id}`}>
                  {r.active ? <Pause className="h-4 w-4 text-[hsl(var(--warning))]" /> : <Play className="h-4 w-4 text-[hsl(var(--success))]" />}
                </Button>
                <Button variant="ghost" size="icon" title={t("common.edit")} onClick={() => onEditRule(r)} data-testid={`exp-rule-edit-${r.id}`}>
                  <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                </Button>
                <Button variant="ghost" size="icon" title={t("common.delete")} onClick={() => setConfirmDel(r)} data-testid={`exp-rule-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="exp-recurring-close">{t("common.close")}</Button>
        </DialogFooter>

        <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
          <AlertDialogContent data-testid="exp-rule-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("expenses.ruleDeleteTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("expenses.ruleDeleteDesc")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="exp-rule-delete-confirm">{t("common.delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
